import type { ChangelogRecord, Release } from '~~/layers/changelog/shared/types/changelog';
import { compareVersions } from './semver';

// Explicit relative import of compareVersions (not the ambient auto-import) so
// these pure functions can be unit-tested by importing this file directly, with
// no Nuxt runtime present.

// The diff window: releases newer than `lastSeen` and no newer than `current`,
// i.e. everything the client has not yet seen up to the version they now run.
// When `lastSeen` is null (brand-new client), every release up to `current` is in
// window. Input order is irrelevant; the caller sorts for display.
export function diffReleases(
	releases: Release[],
	lastSeen: string | null,
	current: string,
): Release[] {
	return releases.filter((release) => {
		if (compareVersions(release.version, current) > 0) return false;
		if (lastSeen === null) return true;
		return compareVersions(release.version, lastSeen) > 0;
	});
}

// Pure transition for the per-client Dexie record given the version the app now
// runs. Three branches, all unit-tested:
//  - no record (first-ever open): seed, mark dismissed (nothing to diff; do not
//    nag a brand-new client), lastSeen null.
//  - same version: unchanged (a later visit; the popover shows only if a prior
//    visit left it not-yet-dismissed).
//  - new version: shift currentVersion down to lastSeenVersion, adopt the new
//    version, and un-dismiss so the popover is offered (the caller still gates on
//    a non-empty diff window before actually showing it).
export function nextChangelogState(
	stored: ChangelogRecord | null | undefined,
	appVersion: string,
): ChangelogRecord {
	if (!stored) {
		return {
			id: 'state',
			lastSeenVersion: null,
			currentVersion: appVersion,
			popoverDismissed: true,
		};
	}

	if (stored.currentVersion === appVersion) {
		return { ...stored, id: 'state' };
	}

	return {
		id: 'state',
		lastSeenVersion: stored.currentVersion,
		currentVersion: appVersion,
		popoverDismissed: false,
	};
}
